import {
  type Credential,
  type ErrorCode,
  type ErrorEntry,
  isMountayaApiError,
  Mountaya,
  PAYMENT_REQUIRED_CODE,
  type ResponseMetadataLimitReached,
  type Surface,
} from "@mountaya/client";
import { z } from "zod";

import type { AuthStrategy, SessionTokenReader } from "./auth";
import { SESSION_AUTH } from "./auth";
import { sessionFetchFor } from "./organizationFetch";

/**
 * The bridge from this app's client config onto the published SDK.
 *
 * The map — itineraries, zones, collections, layers — is served by `mountaya.studio`, the
 * same package customers install, alongside the Data API. Beside it, `mountaya.admin` serves
 * the organization's own administration: its billing, its profile and settings, its
 * membership, its publishable keys, its per-surface usage series, and the AI provider
 * configuration the co-pilot runs on. All of that is on the contract, because managing
 * through the API the account you already own is the point of having an API, an integrator
 * reads their own invoices and rotates their own keys with the same methods this console
 * calls.
 *
 * What is NOT on it is server-side credential material and the console's own plumbing.
 * `./console` binds the generated methods for the first half, the four SECRET-key
 * operations, which mint session tokens and would defeat the split if a browser-reachable
 * client carried them, the transfer of ownership, an owner's act a session minted from such a
 * key must never reach, plus the whole organization RECORD the contract does not describe and
 * the staff-only settings replace. The second half has no generated method on EITHER client
 * and rides `transport.request` directly: the publishable-key lookup an embed makes before it
 * has a credential, the Ory Hydra consent flow, the service health probe, and the signed-in
 * user's preferences and organization list, a person, where every published admin operation
 * is scoped to an organization.
 *
 * Both halves ride the very same client, so there is ONE request pipeline in this
 * application: one credential projection, one retry policy, one date revival, one error
 * taxonomy. A 402 reads the same whichever of the two halves answered it.
 *
 * Dogfooding is the point. If the published surface cannot drive the real studio, that is
 * a defect in the public contract, and it shows up here rather than in somebody else's
 * integration.
 *
 * **Where the line sits, so it does not get re-litigated.** The SDK owns operations, the
 * transport, the error taxonomy, the credential's identity and the CACHE IDENTITY of every
 * resource it publishes a read for — `mountayaKeys` scopes each key to the credential, and
 * `studioResourceKey` is the one place a studio resource becomes one of those keys. What is
 * left here is the retry arithmetic that stops the SDK's two attempts composing with
 * TanStack's three into twelve, and the operations the contract never publishes.
 *
 * `@mountaya/client-react`'s `MountayaProvider` is what delivers a client to React here — see
 * `clientProvider.tsx`, which composes it — so the credential is a property of the SUBTREE
 * rather than of each call site, and this application exercises the same seam a customer
 * does.
 */

/**
 * RestClientConfig is the render-free configuration for the API client. The baseUrl is
 * injected by the consumer (the SPA passes the build-time
 * import.meta.env.VITE_HTTP_INTERNAL_API_URL) so the package stays environment-agnostic.
 * The optional auth strategy selects how requests authenticate — the webapp omits it
 * (defaulting to the Ory-cookie session path), the embed route passes an `embed` strategy
 * carrying the publishable key (+ optional session token). When absent, callers fall back
 * to SESSION_AUTH.
 */
export interface RestClientConfig {
  auth?: AuthStrategy;
  baseUrl: string;
}

/**
 * requestLanguage is the UI language every request announces through Accept-Language. It is
 * module state rather than a field on RestClientConfig because it is a property of the whole
 * app session, not of one call: the config is constructed ad hoc at ~30 call sites, and a
 * field there would regress silently the first time a new site forgot it.
 *
 * null means "say nothing", which leaves the browser's own Accept-Language in place — the
 * correct posture before the viewer's preference has resolved.
 */
let requestLanguage: string | null = null;

/**
 * setRequestLanguage records the UI language subsequent requests announce. The app calls it
 * once from wherever i18next's language is resolved, and again whenever that language
 * changes.
 *
 * It exists because the backend localizes its OWN validation sentences off Accept-Language,
 * and those sentences are spliced into UI copy the app has already translated. Without this
 * the header carries whatever the browser sends, which is independent of the language the app
 * is running in — the two disagree and one message ends up half in each.
 *
 * The api package is an architectural leaf and cannot import i18next, so the language is
 * pushed in rather than read out. A client already built keeps the language it was built
 * with; {@link mountayaFor} keys its cache on this value, so the next call after a change
 * builds the client that announces the new one.
 */
export const setRequestLanguage = (language: string | null): void => {
  requestLanguage = language;
};

/**
 * getRequestLanguage returns the language requests announce, or null when none has been set.
 */
export const getRequestLanguage = (): string | null => requestLanguage;

/**
 * resetRequestLanguage returns the module to its initial "say nothing" state.
 *
 * It is here for tests. Vitest isolates per FILE, not per test, so a spec that sets a
 * language leaves it set for every later test in the same file — and the one that then
 * asserts the browser's own Accept-Language is used passes or fails on the order it happened
 * to run in. A spec calls this from beforeEach rather than remembering to unset what it set.
 */
export const resetRequestLanguage = (): void => {
  requestLanguage = null;
};

/**
 * credentialFor maps an auth strategy onto an SDK credential.
 *
 * The session case is why the SDK carries a cookie credential at all: the webapp
 * authenticates with an Ory `ory_session_*` cookie, and an SPA cannot read an httpOnly
 * cookie to build a header from it. It belongs in the published package rather than here —
 * a customer fronting the API behind their own sign-in, or a backend-for-frontend, has
 * exactly the same need.
 *
 * The cookie is the whole credential: the session it carries names the member every request
 * acts as, so nothing beside it says who that is. A client never picks who it acts as.
 *
 * An embed's session token held as a reader is handed on as the function it is, which the SDK
 * calls for each request: the client follows a token the hosted embed renews, and keys its reads
 * by the session's presence rather than by a value that rotates.
 */
export const credentialFor = (strategy: AuthStrategy): Credential => {
  switch (strategy.kind) {
    case "embed":
      return {
        publishableKey: strategy.publishableKey,
        ...(strategy.sessionToken === undefined ? {} : { sessionToken: strategy.sessionToken }),
      };
    case "share":
      // Cookies ride along deliberately: a share visitor who is ALSO signed in is answered
      // a richer projection, and the share token remains the sole authorization either way.
      return { cookie: true, shareToken: strategy.shareToken };
    case "session":
      return { cookie: true };
    default:
      // Unreachable while AuthStrategy stays a closed union — and that is the point:
      // adding a fourth strategy makes this a compile error naming the SDK credential
      // that has not been decided yet, rather than a runtime surprise.
      throw new Error(`unmapped auth strategy: ${JSON.stringify(strategy)}`);
  }
};

/**
 * mapCredentialFor projects an auth strategy onto the credential a MAP attaches to the
 * requests MapLibre makes for itself.
 *
 * It goes through the SDK rather than mapping the strategy a second time, so a tile request
 * authenticates exactly as the REST call beside it. The client owns the rules that are easy
 * to get subtly wrong: a secret key is never projected, a publishable key never carries a
 * cookie (the cookie's broader session would outrank the scoped key), and a share token
 * keeps the cookie that lets the platform resolve the signed-in viewer's own plan.
 *
 * The base URL is irrelevant to the projection and is left empty deliberately — this
 * answers "what credential", where the tile transform separately owns "which origins get
 * it". The `tiles` surface is named because that is what the answer is for.
 */
export const mapCredentialFor = (strategy: AuthStrategy) =>
  mountayaFor({ auth: strategy, baseUrl: "" }, "tiles").transport.mapCredential;

/**
 * clients caches one Mountaya client per config, keyed by base URL, strategy and language.
 *
 * A client holds a credential and a retry policy, so building one per query-fn call would
 * rebuild both on every render that reaches the cache. The key includes the credential
 * because two credentials are answered different projections of the same resource — the
 * same reason `MountayaTransport.credentialScope` is a mandatory segment of every query key.
 */
const clients = new Map<string, Mountaya>();

/**
 * readerKeys numbers each session token reader a client was built with, for {@link cacheKey}.
 *
 * A reader is keyed by its identity rather than by the token it answers: the token rotates for
 * the reader's whole life, and a key that followed it would build a client per renewal and drop
 * every read made under the last one. Weakly held, so a reader nothing else holds is not kept
 * alive by having once been keyed.
 */
const readerKeys = new WeakMap<SessionTokenReader, number>();
let nextReaderKey = 0;

/**
 * sessionTokenKey is the cache-key segment of an embed strategy's session token: the token
 * itself where it is a string, which never rotates, the reader's own number where it is read per
 * request, and nothing where there is none.
 */
const sessionTokenKey = (sessionToken: string | SessionTokenReader | undefined): string => {
  if (sessionToken === undefined || typeof sessionToken === "string") {
    return sessionToken ?? "";
  }

  let key = readerKeys.get(sessionToken);
  if (key === undefined) {
    nextReaderKey += 1;
    key = nextReaderKey;
    readerKeys.set(sessionToken, key);
  }

  return `reader:${key}`;
};

/**
 * cacheKey identifies one client: the surface it targets, its base URL, its credential, and
 * the language it announces.
 *
 * Language is a key segment because `Accept-Language` is fixed at construction. Without it a
 * viewer who switches locale would keep the client built before the switch and keep reading
 * backend validation sentences in the language they just left. Query keys deliberately do NOT
 * carry it: the header localizes the backend's own validation sentences, not the success
 * payloads, so a cached success entry stays correct across a locale change and re-fetching
 * every resource to change nothing would be pure waste.
 */
const cacheKey = (config: RestClientConfig, surface: Surface | string): string => {
  const strategy = config.auth ?? SESSION_AUTH;
  const language = requestLanguage ?? "";

  switch (strategy.kind) {
    case "embed":
      return `${surface}|${config.baseUrl}|embed|${strategy.publishableKey}|${sessionTokenKey(strategy.sessionToken)}|${language}`;
    case "share":
      return `${surface}|${config.baseUrl}|share|${strategy.shareToken}|${language}`;
    case "session":
      return `${surface}|${config.baseUrl}|session|${language}`;
    default:
      throw new Error(`unmapped auth strategy: ${JSON.stringify(strategy)}`);
  }
};

/**
 * mountayaFor returns the SDK client for a config and surface, building it once.
 *
 * Retry is the SDK's own, deliberately. It is the only layer that can see a Retry-After
 * header and a status code, so it is the only layer that can tell a 429 worth waiting on
 * from a 403 that will answer the same way forever — and it already refuses to repeat a
 * network failure for a non-idempotent request, because a dropped socket does not say
 * whether the write landed.
 *
 * What must not happen is both layers retrying: TanStack's default of three composed with
 * the SDK's two is twelve requests for one failed call. `shouldRetryQuery` in ./queryClient
 * is the other half of this decision and holds the arithmetic.
 */
export const mountayaFor = (config: RestClientConfig, surface: Surface = "rest"): Mountaya => {
  const key = cacheKey(config, surface);
  const existing = clients.get(key);
  if (existing !== undefined) {
    return existing;
  }

  const strategy = config.auth ?? SESSION_AUTH;
  const client = new Mountaya({
    // The surface decides which base URL the config's own baseUrl fills. The three
    // surfaces are three services on three hosts, and a config is built for one of them —
    // a tile specification is fetched with the TILE base URL even though it arrives here
    // in a struct named RestClientConfig.
    baseUrls: { [surface]: config.baseUrl },
    credential: credentialFor(strategy),
    ...sessionFetchFor(strategy, surface === "rest" ? [] : [config.baseUrl]),
    ...(requestLanguage === null ? {} : { language: requestLanguage }),
  });

  clients.set(key, client);

  return client;
};

/**
 * MountayaSurfacesConfig is one credential reaching more than one service.
 *
 * `mountayaFor` builds a client per surface because the console's reads each have one base URL
 * and one surface. Everything the studio does is the exception: a dispatch persists over REST
 * and then re-reads the Data API to analyse what it wrote, a viewer registers a tile
 * specification while the same tree reads the itinerary behind it, and threading a client per
 * surface meant a credential mapping and a retry policy per surface for what is one credential.
 *
 * `rest` is required because every surface of this application reads it. The other two are
 * optional so a caller states the services it actually reaches — a client carrying a base URL
 * for a service it never calls is a URL nobody will notice is wrong.
 */
export interface MountayaSurfacesConfig {
  auth?: AuthStrategy;
  /** The Data API's base URL — host and scheme only; the client appends the GraphQL path. */
  dataUrl?: string;
  /** The REST API's base URL. */
  restUrl: string;
  /** The tile service's base URL. A tile credential attaches to THIS origin and no other. */
  tilesUrl?: string;
}

/**
 * mountayaSurfaces returns the one client a multi-service surface reaches through.
 *
 * Cached on the same terms as {@link mountayaFor} and in the same map. The cache segment names
 * every url the client was built with, because the set of services is what makes one of these
 * different from another — two clients for the same credential and the same REST host are still
 * two clients if only one of them can reach the tiles.
 */
export const mountayaSurfaces = (config: MountayaSurfacesConfig): Mountaya => {
  const baseUrls = {
    ...(config.dataUrl === undefined ? {} : { data: config.dataUrl }),
    rest: config.restUrl,
    ...(config.tilesUrl === undefined ? {} : { tiles: config.tilesUrl }),
  };
  const key = cacheKey(
    { auth: config.auth, baseUrl: config.restUrl },
    `surfaces|${Object.entries(baseUrls)
      .map(([surface, url]) => `${surface}=${url}`)
      .join(",")}`,
  );
  const existing = clients.get(key);
  if (existing !== undefined) {
    return existing;
  }

  const strategy = config.auth ?? SESSION_AUTH;
  const client = new Mountaya({
    baseUrls,
    credential: credentialFor(strategy),
    // The Data API and the Tile API record a console session's requests under the organization
    // it has open; the REST API's are its own resources' and need no header.
    ...sessionFetchFor(strategy, [config.dataUrl, config.tilesUrl]),
    ...(requestLanguage === null ? {} : { language: requestLanguage }),
  });

  clients.set(key, client);

  return client;
};

/**
 * carriesCode reports whether any entry of a platform failure carries the given code.
 *
 * It reads the entries as data and calls no method of the error, so nothing here can throw
 * inside the catch handler that asked, in place of the error being handled.
 * `isMountayaApiError` narrows to a value that holds what the class adds, whichever copy of the
 * package built it, and refuses a failure that crossed a serialization (a persisted cache, a
 * message between two windows): every predicate answers false for one.
 */
const carriesCode = (error: { readonly errors: readonly ErrorEntry[] }, code: ErrorCode): boolean =>
  error.errors.some((entry) => entry.extensions?.code === code);

/**
 * isPaymentRequiredError reports whether a thrown value is a 402: a 402 status, or an envelope
 * carrying the SDK's `PAYMENT_REQUIRED_CODE`, which every 402 carries. It is said about the
 * organization's agreement or its billing details: a product it does not hold, a limit of its
 * agreement (the seats at an invite, the published itineraries at a publish), or, on the Data
 * API, a self-serve organization with no payment method on file. Reading both signals keeps the
 * detection robust to either one the backend leads with. Any other throw (a network failure, a
 * different envelope) returns false.
 */
export const isPaymentRequiredError = (error: unknown): boolean =>
  isMountayaApiError(error) && (error.status === 402 || carriesCode(error, PAYMENT_REQUIRED_CODE));

/**
 * LIMIT_REACHED_CODE is the per-entry code the API stamps on every refusal of a ceiling, on the
 * status the refusal travels with (ADR 0105): a 402 for the seats and the published itineraries
 * an agreement allows, a 403 for the itineraries a personal space keeps.
 */
const LIMIT_REACHED_CODE: ErrorCode = "LIMIT_REACHED";

/**
 * isSeatLimitError reports whether a thrown value is the 402 a member invitation is refused
 * with when the organization's agreement has no seat left for it: a 402 whose entry carries
 * `LIMIT_REACHED`. The seats are the one ceiling an invitation meets, and the code is what tells
 * it from a product the organization does not hold, which is a 402 too; the refusal's key is
 * localized into the entry's message on the way out and never reaches the wire. Any other throw
 * returns false.
 */
export const isSeatLimitError = (error: unknown): boolean =>
  isMountayaApiError(error) && error.status === 402 && carriesCode(error, LIMIT_REACHED_CODE);

/**
 * limitReachedSchema parses the metadata every `LIMIT_REACHED` refusal carries: which ceiling
 * refused the write, how many it allows and how many are held now. `limit` is the contract's
 * kebab-case `LimitKey`, which names ceilings no agreement sets (the pending invitations, the
 * personal space's itineraries) beside the ones it does.
 */
const limitReachedSchema = z.object({
  allowed: z.number(),
  limit: z.enum([
    "pending-invitations",
    "personal-itineraries",
    "published-itineraries",
    "published-zones",
    "seats",
  ]),
  used: z.number(),
}) satisfies z.ZodType<ResponseMetadataLimitReached>;

/**
 * limitReachedOf returns what a `LIMIT_REACHED` refusal says about the ceiling it met (a 402 for
 * a ceiling of the agreement, a 403 for one no agreement raises), or undefined for any other
 * throw. The metadata is read as data and parsed, so a refusal that carried none, or one in a
 * shape this console does not know, answers undefined rather than a guess.
 */
export const limitReachedOf = (error: unknown): ResponseMetadataLimitReached | undefined => {
  if (!isMountayaApiError(error) || !carriesCode(error, LIMIT_REACHED_CODE)) {
    return undefined;
  }

  const parsed = limitReachedSchema.safeParse(error.metadata);

  return parsed.success ? parsed.data : undefined;
};

/**
 * conflictMetadataOf returns the metadata a 409 carries, read as data for the caller to parse
 * against the shape its operation documents (an invitation's 409 names the member the address
 * belongs to, or the invitation already waiting for it), or undefined for any other throw. A 409
 * coded `IDEMPOTENCY_KEY_IN_USE` carries none: the client waits it out and sends the request
 * again itself, and raises it only once its wait is spent ({@link isWriteStillRunningError}).
 */
export const conflictMetadataOf = (error: unknown): unknown =>
  isMountayaApiError(error) && error.status === 409 ? error.metadata : undefined;

/**
 * ANSWER_UNAVAILABLE_CODE is the per-entry code of the 409 a repeat of an `Idempotency-Key` is
 * answered with when the write it carried WAS MADE and its first answer cannot be given again: a
 * 5xx the API answered after the write, or an answer too large to keep.
 */
const ANSWER_UNAVAILABLE_CODE: ErrorCode = "IDEMPOTENCY_ANSWER_UNAVAILABLE";

/**
 * KEY_IN_USE_CODE is the per-entry code of the 409 a repeat of an `Idempotency-Key` is answered
 * with while the first request sent with it is still running.
 */
const KEY_IN_USE_CODE: ErrorCode = "IDEMPOTENCY_KEY_IN_USE";

/**
 * isAnswerUnavailableError reports whether a thrown value is the 409 coded
 * `IDEMPOTENCY_ANSWER_UNAVAILABLE`: **the write was made**. It is no refusal and no conflict, so
 * a surface never shows it as a failure and never offers to send the write again (under a new key
 * that would make it twice): it reads the resource again, at {@link createdLocationOf} where the
 * write created one. The client reaches it on its own repeat of a keyed write after a 502, 503 or
 * 504, so a create whose answer was lost after it committed arrives here. Any other throw returns
 * false.
 */
export const isAnswerUnavailableError = (error: unknown): boolean =>
  isMountayaApiError(error) && error.status === 409 && carriesCode(error, ANSWER_UNAVAILABLE_CODE);

/**
 * isWriteStillRunningError reports whether a thrown value is the 409 coded
 * `IDEMPOTENCY_KEY_IN_USE` the client raises once its own wait for the first request is spent:
 * the write is still being made, so a surface says so and reads the resource again shortly,
 * rather than inviting a resend. Any other throw returns false.
 */
export const isWriteStillRunningError = (error: unknown): boolean =>
  isMountayaApiError(error) && error.status === 409 && carriesCode(error, KEY_IN_USE_CODE);

/**
 * isWriteMadeOrRunning reports whether a thrown value is either idempotency 409: the write was
 * made and its answer lost ({@link isAnswerUnavailableError}), or it is still being made
 * ({@link isWriteStillRunningError}). Either way what it changes is stale where a page holds it,
 * so a hook reads it again exactly as it does after a write that answered. Any other throw
 * returns false.
 */
export const isWriteMadeOrRunning = (error: unknown): boolean =>
  isAnswerUnavailableError(error) || isWriteStillRunningError(error);

/**
 * isRefusedOutright reports whether a thrown value is a refusal that made nothing: an answer of
 * the 4xx family other than the two idempotency 409s ({@link isAnswerUnavailableError},
 * {@link isWriteStillRunningError}), which say the write was made or is still being made. The API
 * keeps a refusal against the `Idempotency-Key` it was sent under, so a caller holding one key
 * across its resubmits draws a fresh key after this, since a resubmit with other details under
 * the refused key would be refused as a reused key. Anything else (a 5xx, a dropped connection,
 * the idempotency 409s) may have made the write, and the key is held. Any non-API throw returns
 * false.
 */
export const isRefusedOutright = (error: unknown): boolean =>
  isMountayaApiError(error) &&
  error.status >= 400 &&
  error.status < 500 &&
  !isAnswerUnavailableError(error) &&
  !isWriteStillRunningError(error);

/**
 * createdLocationOf returns the `Location` the write's first answer carried, the API path of what
 * it created relative to the API's host (`/v1/organizations/{id}`), off the 409 coded
 * `IDEMPOTENCY_ANSWER_UNAVAILABLE`; undefined for any other throw, and for a write whose first
 * answer named none. A create notes it the moment it inserts, so a 5xx answered after the commit
 * still names what it created.
 */
export const createdLocationOf = (error: unknown): string | undefined => {
  if (!isAnswerUnavailableError(error) || !isMountayaApiError(error)) {
    return undefined;
  }

  const location: unknown =
    typeof error.metadata === "object" && error.metadata !== null && "location" in error.metadata
      ? error.metadata.location
      : undefined;

  return typeof location === "string" && location.length > 0 ? location : undefined;
};

/**
 * createdIdOf returns the id the created resource's `Location` ends in, when that location is
 * under the collection the caller names (`/v1/organizations`, or an organization's
 * `/secret-keys`), and undefined otherwise: for any other throw, a write that named no location,
 * and a location under another collection, which is never guessed at.
 *
 * @param error - What the write threw.
 * @param collection - The path the created resource's id follows, without a trailing slash.
 */
export const createdIdOf = (error: unknown, collection: string): string | undefined => {
  const location = createdLocationOf(error);
  if (location === undefined) {
    return undefined;
  }

  const path = location.split(/[?#]/, 1)[0] ?? "";
  if (!path.startsWith(`${collection}/`)) {
    return undefined;
  }

  const id = path.slice(collection.length + 1);

  return id.length > 0 && !id.includes("/") ? decodeURIComponent(id) : undefined;
};

/**
 * isPreconditionFailedError reports whether a thrown value is the 412 a conditional write is
 * refused with: its `ifMatch` named a version the resource no longer carries, because somebody
 * changed it since it was read. Nothing was written, and the same request would be refused the
 * same way: the surface reloads the resource and says so, and the person decides again. Any
 * other throw returns false.
 */
export const isPreconditionFailedError = (error: unknown): boolean =>
  isMountayaApiError(error) && error.status === 412;

/**
 * CONFLICT_CODE is the per-entry code of a 409 the state of the resource answered: what the
 * request asked for is no longer possible, because somebody changed it since it was read. The
 * other 409s are about the request rather than the resource, and carry codes of their own: an
 * idempotency key still in use ({@link isWriteStillRunningError}), which the client waits out
 * and raises only once its wait is spent, and a write made whose answer was lost
 * ({@link isAnswerUnavailableError}).
 */
const CONFLICT_CODE: ErrorCode = "CONFLICT";

/**
 * isConflictError reports whether a thrown value is a 409 coded `CONFLICT`: the state the request
 * met forbids it, a first configuration somebody else created since the page read none, for one.
 * Nothing was written, and the same request would be refused the same way: the surface reloads
 * the resource and says so, as it does for a 412. A 409 coded otherwise and any other throw return
 * false.
 */
export const isConflictError = (error: unknown): boolean =>
  isMountayaApiError(error) && error.status === 409 && carriesCode(error, CONFLICT_CODE);

/**
 * isBadRequestError reports whether a thrown value is a 400: the API refused what the request
 * carried, a value or a combination of values it does not take. Nothing was done, and the same
 * request would be refused the same way. Any other throw returns false.
 */
export const isBadRequestError = (error: unknown): boolean =>
  isMountayaApiError(error) && error.status === 400;

/**
 * isNotFoundError reports whether a thrown value is a 404: what the request named is not there,
 * or no longer is, an invitation answered elsewhere, withdrawn or lapsed since the page listed it.
 * Any other throw returns false.
 */
export const isNotFoundError = (error: unknown): boolean =>
  isMountayaApiError(error) && error.status === 404;

/**
 * isRateLimitError reports whether a thrown value is a 429: a limit of the API is spent for now.
 * Any other throw returns false.
 */
export const isRateLimitError = (error: unknown): boolean =>
  isMountayaApiError(error) && error.status === 429;

/**
 * retryAfterOf returns the whole seconds a 429 asked the caller to wait, from the `Retry-After`
 * every 429 carries, or undefined for any other throw or a 429 that named no wait. A surface
 * states it ("try again in 40 seconds") rather than a vague "later".
 */
export const retryAfterOf = (error: unknown): number | undefined => {
  if (!isRateLimitError(error) || typeof error !== "object" || error === null) {
    return undefined;
  }

  const wait: unknown = "retryAfter" in error ? error.retryAfter : undefined;

  return typeof wait === "number" && Number.isFinite(wait) && wait >= 0 ? wait : undefined;
};

/**
 * isGatewayTimeoutError reports whether a thrown value is the 504 the API answers with when
 * a request outran its own budget rather than failing on its merits. It is worth its own
 * predicate because the two read completely differently to the person waiting: a 500 means
 * the call did not happen, while a 504 means it may have half happened and the honest advice
 * is to reload and look before retrying. Any other throw returns false.
 */
export const isGatewayTimeoutError = (error: unknown): boolean =>
  isMountayaApiError(error) && error.status === 504;

/**
 * SAFETY_RULES_FIELD is the first segment of the path a staff-controlled refusal is answered at:
 * the `safety_rules` group of a settings PATCH, then the rule it names.
 */
const SAFETY_RULES_FIELD = "safety_rules";

/**
 * isStaffControlledError reports whether a thrown value is the 403 an administrator's settings
 * write is refused with when it would change a safety rule the organization's agreement fixes
 * (the overlay allowlist, whether an embed may switch overlays, and the domains of an
 * organization billed by agreement): a 403 whose entry is answered at a rule of the
 * `safety_rules` group. The refusal's key never reaches the wire, so the path is what tells it
 * from every other 403. Any other throw returns false.
 */
export const isStaffControlledError = (error: unknown): boolean =>
  isMountayaApiError(error) &&
  error.status === 403 &&
  error.errors.some((entry) => entry.path?.includes(SAFETY_RULES_FIELD) === true);

/**
 * restValidationMessage returns the localized reason a request was refused for on its own
 * fields, or null for anything else: the first entry of a 400, and the first entry answered at
 * a field of a 403 (a staff-controlled safety rule, see {@link isStaffControlledError}). The
 * backend resolves a validation key into user-facing copy in the caller's language before it
 * ever reaches the wire (the key itself is NOT on the wire), so a call site that swallows this
 * and shows its own generic string is discarding the only specific explanation anyone will get.
 *
 * It is deliberately limited to those two. A 5xx envelope carries an OPAQUE message by design,
 * and a 403 answered at no field is the role refusal, whose sentence says nothing about what was
 * sent: surfacing either would replace a contextual local string with a vaguer server one.
 */
export const restValidationMessage = (error: unknown): string | null => {
  if (!isMountayaApiError(error)) {
    return null;
  }

  const entries: readonly ErrorEntry[] = error.errors;
  if (error.status === 400) {
    return entries[0]?.message ?? null;
  }

  if (error.status === 403) {
    return entries.find((entry) => (entry.path?.length ?? 0) > 0)?.message ?? null;
  }

  return null;
};

/**
 * restRefusalMessage returns the sentence the API refused a request with, or null when it gave
 * none worth showing: the first entry of a 400, a 403 or a 409, whatever field it is answered
 * at. The backend localizes each refusal into the caller's language before it reaches the wire,
 * so on a surface whose refusals all say something the reader can act on (billing: a tax location
 * to add, a promotion code no longer valid, an organization billed by agreement, a billing account
 * not set up yet) this sentence is better than any generic copy the surface carries.
 *
 * It answers null for a 5xx, whose body is opaque by design, and for every other status, whose
 * surface supplies its own words. It is wider than {@link restValidationMessage}, which keeps a
 * 403 answered at no field to itself: a surface whose role refusal would replace a contextual
 * string reads that one instead.
 *
 * It answers null for the two idempotency 409s too, which are no refusal: one says the write was
 * made ({@link isAnswerUnavailableError}), the other that it is still being made
 * ({@link isWriteStillRunningError}), and their sentence is written for a developer. A surface
 * reads either with its predicate and says what happened in its own words.
 */
export const restRefusalMessage = (error: unknown): string | null => {
  if (
    !isMountayaApiError(error) ||
    isAnswerUnavailableError(error) ||
    isWriteStillRunningError(error)
  ) {
    return null;
  }

  if (error.status === 400 || error.status === 403 || error.status === 409) {
    return error.errors[0]?.message ?? null;
  }

  return null;
};

/**
 * restPaymentRequiredMessage returns the sentence a 402 was answered with, or null for any other
 * answer. The API localizes it like every refusal, and on a billing write it names which of the
 * facts a 402 stands for applies: no payment method on file, or invoices still owed that the
 * payment method could not pay, by this organization or by another one billed on the same card.
 * The status and its code are the same for both, so the sentence is what tells the reader which
 * to put right.
 */
export const restPaymentRequiredMessage = (error: unknown): string | null =>
  isMountayaApiError(error) && error.status === 402 ? (error.errors[0]?.message ?? null) : null;

/**
 * restNotFoundMessage returns the sentence a 404 was answered with, or null for any other answer.
 * The API localizes it like every refusal, and on an operation that names what it found missing
 * it says more than a surface's own copy could: a billing write answers "no billing account yet"
 * here, for an organization whose billing was never set up. Ending a Data API subscription that
 * has already ended is no such refusal: the API answers it 200, with the subscription as it
 * stands.
 */
export const restNotFoundMessage = (error: unknown): string | null =>
  isMountayaApiError(error) && error.status === 404 ? (error.errors[0]?.message ?? null) : null;

/**
 * REQUEST_BODY is where the API answers a refused field of a request body: `["request", "body",
 * …]`, the path the transport prefixes onto every field a domain refuses.
 */
const REQUEST_BODY = ["request", "body"] as const;

/**
 * bodyRefusal returns the entry a refusal of the given status carries at a field of the request
 * body, or undefined for any other answer. The field is matched by name anywhere under the body,
 * so `email` finds the entry at `["request", "body", "email"]` and `tax_id` the one at
 * `["request", "body", "tax_id", "value"]`.
 *
 * The status and the path are what a client classifies a refusal by. The key the backend raised
 * it with is localized into the entry's sentence on the way out and never reaches the wire, so a
 * check that looked for the key would never match.
 *
 * Every predicate in this module classifies through the SDK's `isMountayaApiError`, which reads
 * the name an error's class declares and never its constructor. A second copy of
 * `@mountaya/client` in the bundle is a second set of classes, and a constructor check against
 * one refuses every failure the other throws.
 */
export const bodyRefusal = (
  error: unknown,
  status: number,
  field: string,
): ErrorEntry | undefined => bodyRefusals(error, status, field)[0];

/**
 * bodyRefusals returns every entry a refusal of the given status carries at a field of the
 * request body, in the order the API answered them, or none for any other answer. It is
 * {@link bodyRefusal} for a field the API refuses once per member: a list whose refused entries
 * are each answered at their index, as `["request", "body", "safety_rules", "with_overlays", 2]`
 * is for the third entry of an overlay allowlist.
 */
export const bodyRefusals = (
  error: unknown,
  status: number,
  field: string,
): readonly ErrorEntry[] => {
  if (!isMountayaApiError(error) || error.status !== status) {
    return [];
  }

  return error.errors.filter(
    (entry) =>
      entry.path?.[0] === REQUEST_BODY[0] &&
      entry.path[1] === REQUEST_BODY[1] &&
      entry.path.slice(REQUEST_BODY.length).includes(field),
  );
};

/**
 * paramRefusal returns the entry a refusal of the given status carries at a path parameter of the
 * request, `["request", "params", <name>]`, or undefined for any other answer. The edge answers a
 * path parameter the contract's pattern refuses there, before any handler runs: a promotion code of
 * any other shape than the contract's is one, at `["request", "params", "code"]`.
 */
export const paramRefusal = (
  error: unknown,
  status: number,
  name: string,
): ErrorEntry | undefined => {
  if (!isMountayaApiError(error) || error.status !== status) {
    return undefined;
  }

  return error.errors.find(
    (entry) =>
      entry.path?.[0] === "request" && entry.path[1] === "params" && entry.path[2] === name,
  );
};

/**
 * queryRefusal returns the entry a refusal of the given status carries at a query-string
 * parameter of the request, `["request", "query", <name>]`, or undefined for any other answer. A
 * usage read whose `start_at` and `end_at` form no window it reads (one longer than the API's 365
 * days, or one that ends before it starts) is answered at both parameters.
 */
export const queryRefusal = (
  error: unknown,
  status: number,
  name: string,
): ErrorEntry | undefined => {
  if (!isMountayaApiError(error) || error.status !== status) {
    return undefined;
  }

  return error.errors.find(
    (entry) => entry.path?.[0] === "request" && entry.path[1] === "query" && entry.path[2] === name,
  );
};

/**
 * isPathlessRefusal reports whether a thrown value is a refusal of the given status whose entries
 * name no field at all: the request was well formed, and what refused it is the state it met
 * rather than anything it carried. A card attach refused because the promotion code recorded for
 * the organization can no longer be used is one: the code is not a field of that request. Any
 * other throw returns false.
 */
export const isPathlessRefusal = (error: unknown, status: number): boolean =>
  isMountayaApiError(error) &&
  error.status === status &&
  error.errors.length > 0 &&
  error.errors.every((entry) => (entry.path?.length ?? 0) === 0);

/**
 * isServiceUnavailableError reports whether a thrown value is a 503. A billing write answers one
 * when another change to the same organization's billing held it past its wait, and any request
 * does when the API could not validate the console session yet, which the transport has already
 * waited out and asked again by the time a caller sees it: either way nothing was done, and the
 * same request can be sent again in a moment. Its body is opaque like every 5xx, and the two
 * carry the same code, so the surface says that in its own words. Any other throw returns false.
 */
export const isServiceUnavailableError = (error: unknown): boolean =>
  isMountayaApiError(error) && error.status === 503;

/**
 * isForbiddenError reports whether a thrown value is a 403: the caller may not do or read this,
 * whatever it sent. A surface whose read is an owner's or an admin's says so in its own words
 * rather than showing a 403 as an empty or a failed list. Any other throw returns false.
 */
export const isForbiddenError = (error: unknown): boolean =>
  isMountayaApiError(error) && error.status === 403;
